import {
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import {
  isArray,
  isFinite,
  isInteger,
  isNumber,
  isObject,
  isString,
} from 'lodash-es';

import { email, uuid } from '../../../constants/regex';
import { readField } from '../../../utils/read_field';
import relativeTimeToDate from '../../calendar/relative_time_to_date';

type CompareDateValueOperator = typeof isAfter | typeof isBefore;

export type ValidationFunctionParam = string | number | RegExp;

/**
 * Only equalsField reads the model, and it reads one named field. Any object
 * shape works, including the generated API interfaces forms are built from.
 */
export type ValidatableModel = object;

export type ValidatableValue = unknown;

type ValidationFunction = (
  model: ValidatableModel,
  value: ValidatableValue,
  param?: ValidationFunctionParam,
) => boolean;

const compareDateValue = (
  value: string,
  reference: string,
  operation: CompareDateValueOperator,
): boolean => {
  const date = startOfDay(parseISO(value));
  const referenceDate = startOfDay(parseISO(reference));
  if (isValid(date)) {
    // regular date
    return operation(date, referenceDate) && !isSameDay(date, referenceDate);
  }
  // relative date
  const relative = relativeTimeToDate(value);
  return (
    operation(relative, referenceDate) && !isSameDay(relative, referenceDate)
  );
};

// A date field holds either a plain date string or a { min, max } range.
const dateRangeBound = (
  value: ValidatableValue,
  bound: 'min' | 'max',
): string | undefined => {
  if (!isObject(value)) {
    return undefined;
  }
  const candidate = readField(value, bound);
  return isString(candidate) ? candidate : undefined;
};

const minDate: ValidationFunction = (_values, value, min) => {
  const boundary = dateRangeBound(value, 'min');
  if (boundary && isString(min)) {
    return !compareDateValue(boundary, min, isBefore);
  }
  if (isString(value) && value && isString(min)) {
    return !compareDateValue(value, min, isBefore);
  }

  return true;
};

const maxDate: ValidationFunction = (_values, value, max) => {
  const boundary = dateRangeBound(value, 'max');
  if (boundary && isString(max)) {
    return !compareDateValue(boundary, max, isAfter);
  }
  if (isString(value) && value && isString(max)) {
    return !compareDateValue(value, max, isAfter);
  }

  return true;
};

const required: ValidationFunction = (_values, value) => {
  if (!value && value !== 0) {
    return false;
  }

  if (isArray(value) && value.length === 0) {
    return false;
  }
  if (isObject(value) && Object.keys(value).length === 0) {
    return false;
  }

  return true;
};

const preventOnlySpaces: ValidationFunction = (_values, value) => {
  if (!value && value !== 0) {
    return false;
  }

  if (isArray(value) && value.length === 0) {
    return false;
  }
  if (isObject(value) && Object.keys(value).length === 0) {
    return false;
  }

  if (!value.toString().trim()) {
    return false;
  }

  return true;
};

const isEmail: ValidationFunction = (_values, value) =>
  // http://emailregex.com/
  !value ||
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    String(value),
  );

const isPassword: ValidationFunction = (_values, value) =>
  !value || (String(value).length >= 8 && /^[^\s].*[^\s]$/.test(String(value)));

const matchRegexp: ValidationFunction = (_values, value, param) =>
  !value || (param instanceof RegExp && param.test(String(value)));

const minLength: ValidationFunction = (_values, value, param) => {
  if (isArray(value) || isString(value)) {
    return value.length >= Number(param);
  }
  return true;
};

const equalsField: ValidationFunction = (values, value, param) =>
  !value || readField(values, String(param)) === value;

const isEmptyArray: ValidationFunction = (values, value) =>
  required(values, value);

const minNumber: ValidationFunction = (_values, value, param) => {
  if (value === '' || param === undefined) {
    return true;
  }
  if (isString(value)) {
    return parseFloat(value) >= Number(param);
  }
  if (isNumber(value)) {
    return value >= Number(param);
  }
  return true;
};

const maxNumber: ValidationFunction = (_values, value, param) => {
  if (value === '' || param === undefined) {
    return true;
  }
  if (isString(value)) {
    return parseFloat(value) <= Number(param);
  }
  if (isNumber(value)) {
    return value <= Number(param);
  }
  return true;
};

const hasStringValue = (item: unknown): item is { value: string } =>
  isObject(item) && 'value' in item && isString(item.value);

const containsOnlyEmailsOrUUIDs: ValidationFunction = (_values, value) => {
  if (!value || !Array.isArray(value) || !value.length) {
    return true;
  }
  return value.every(
    (item) =>
      hasStringValue(item) && (email.test(item.value) || uuid.test(item.value)),
  );
};

const containsUUID: ValidationFunction = (_values, value) => {
  if (value === '') {
    return true;
  }

  if (typeof value === 'string') {
    return uuid.test(value);
  }

  return false;
};

const maxWords: ValidationFunction = (_values, value, param) => {
  if (!Array.isArray(value) || !value.length) {
    return true;
  }
  return value.every(
    (phrase) => String(phrase).split(/\s+/).length <= Number(param),
  );
};

const validInteger: ValidationFunction = (_values, value) => {
  const number = Number(value);

  return isInteger(number);
};

const validFloat: ValidationFunction = (_values, value) => {
  const number = Number(value);

  return isFinite(number);
};

const validations = {
  maxDate,
  minDate,
  required,
  preventOnlySpaces,
  isEmptyArray,
  isEmail,
  isPassword,
  matchRegexp,
  minLength,
  equalsField,
  minNumber,
  maxNumber,
  containsOnlyEmailsOrUUIDs,
  containsUUID,
  maxWords,
  validInteger,
  validFloat,
};

export type ValidationName = keyof typeof validations;

export default validations;
